import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import {
  useHistory,
  useLocation,
} from 'react-router';
import { Link } from 'react-router-dom';
import {
  Input,
  FormGroup,
  Label,
} from 'reactstrap';
import _ from 'lodash';
import { useIntl } from 'react-intl';

import {
  fetchFiles,
  clearFilesList,
  addSelectedFilter,
  removeSelectedFilter,
} from '../../actions/Files';
import { setSearchFields } from '../../actions/Search';

import { slugify } from '../../helpers';
import { userHasFeature } from '../../helpers/Features';
import { getSetting } from '../../helpers/Settings';
import {
  handleComponentParams,
  handleURLParams,
} from '../../helpers/URLParams';

import {
  fetchCollections,
  clearCollectionsList,
  setCollectionsParams,
} from '../../actions/Collections';

import useResponsiveItemsPerPage from '../../hooks/useResponsiveItemsPerPage';

const FiltersSearchFields = ({
  customFields,
  search,
  type,
  setFilter,
  removeFilter,
  setSearchSelectedFields,
  customSetSearchFields,
  customRemoveSearchFields,
  customSelectedSearchFields,
  prefix,
  external,
}) => {
  const [selectedFields, setSelectedFields] = useState([]);
  const [resetSearchFields, setResetSearchFields] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const itemsPerPage = useResponsiveItemsPerPage();

  const availableFilters = useMemo(() => {
    const filters = [
      ...(type !== 'files' ? [{ key: 'collection_names', label: intl.formatMessage({ defaultMessage: 'Collection names' }) }] : []),
      ...(type !== 'files' ? [{ key: 'collection_descriptions', label: intl.formatMessage({ defaultMessage: 'Collection descriptions' }) }] : []),
      ...(type !== 'collections' ? [{ key: 'file_names', label: intl.formatMessage({ defaultMessage: 'File names' }) }] : []),
      ...(type !== 'collections' ? [{ key: 'file_descriptions', label: intl.formatMessage({ defaultMessage: 'File descriptions' }) }] : []),
      ...(type !== 'collections' ? [{ key: 'tags', label: intl.formatMessage({ defaultMessage: 'Tags' }) }] : []),
    ];

    if (type !== 'collections') {
      if (customFields.length > 0) {
        filters.push({ key: 'custom_fields', label: intl.formatMessage({ defaultMessage: 'Custom fields' }) });
      }
      filters.push({
        key: 'people',
        label: intl.formatMessage({ defaultMessage: 'People' }),
        disabled: !getSetting('face_recognition'),
        locked: !userHasFeature('face-recognition'),
        url: '/admin/settings/ai-tagging',
      });
      filters.push({
        key: 'extracted_text_document',
        label: intl.formatMessage({ defaultMessage: 'Document content' }),
        disabled: !getSetting('search_document_content_documents'),
        locked: !userHasFeature('search-document-content'),
        url: '/admin/settings/search',
      });
      filters.push({
        key: 'extracted_text_image',
        label: intl.formatMessage({ defaultMessage: 'Image content' }),
        disabled: !getSetting('search_document_content_images'),
        locked: !userHasFeature('search-document-content'),
        url: '/admin/settings/search',
      });
      filters.push({
        key: 'video_transcription',
        label: intl.formatMessage({ defaultMessage: 'Video content' }),
        disabled: !getSetting('search_document_content_videos'),
        locked: !userHasFeature('search-video-content'),
        url: '/admin/settings/search',
      });
      filters.push({
        key: 'audio_transcription',
        label: intl.formatMessage({ defaultMessage: 'Audio content' }),
        disabled: !getSetting('search_document_content_audio'),
        locked: !userHasFeature('search-video-content'),
        url: '/admin/settings/search',
      });
    }

    return filters;
  }, [customFields, intl, type]);

  useEffect(() => {
    if (selectedFields.length > 0 && !resetSearchFields) {
      return;
    }

    const af = availableFilters
      .filter(f => f.key !== '')
      .filter(f => !f.disabled);
    if (af.length > 0) {
      setSelectedFields(af.map(f => f.key));
      setResetSearchFields(false);
    }
  }, [availableFilters, selectedFields.length, type, resetSearchFields]);

  useEffect(() => {
    if (customSetSearchFields && customRemoveSearchFields) {
      setSelectedFields(customSelectedSearchFields);
    } else if (external) {
      const fieldsFromUrl = new URLSearchParams(location.search).get('search_fields');
      if (fieldsFromUrl) setSelectedFields(fieldsFromUrl.split(','));
    } else if (
      location.pathname === '/results' ||
      location.pathname === '/files' ||
      location.pathname === '/collections' ||
      location.pathname === '/archive/files' ||
      location.pathname === '/archive/collections'
    ) {
      const searchParams = new URLSearchParams(location.search);
      const searchFromUrl = searchParams.get('search');

      handleComponentParams(
        true,
        ['search_fields'],
        null,
        [(searchFieldsFromUrl) => {
          if (
            searchFromUrl &&
            !_.isEqual(searchFieldsFromUrl, selectedFields) &&
            !_.isEmpty(searchFieldsFromUrl)
          ) {
            setSelectedFields(searchFieldsFromUrl);
          } else if (
            (!(search || searchFromUrl) &&
            ((!_.isEmpty(searchFieldsFromUrl) || !_.isEmpty(selectedFields)))) ||
            (search && !searchFromUrl &&
              !_.isEmpty(selectedFields) && _.isEmpty(searchFieldsFromUrl))
          ) {
            removeFilter('search_fields');
            setResetSearchFields(true);
            if (searchParams.has('search_fields')) {
              setSelectedFields([]);
              handleURLParams({ search_fields: null }, history);
            }
          }
        }],
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const handleChangeFilter = useCallback((e) => {
    const { checked, value } = e.target;
    const newSelectedFields = checked ?
      [...selectedFields, value] :
      selectedFields.filter(s => s !== value);

    const label = value.toLowerCase().replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

    if (customSetSearchFields && customRemoveSearchFields) {
      if (checked) {
        customSetSearchFields(value, label);
      } else {
        customRemoveSearchFields('search_fields', value);
      }
      setSelectedFields(customSelectedSearchFields);
    } else {
      if (checked) {
        setFilter({
          type: 'search_fields',
          label,
          key: value,
        });
      } else {
        removeFilter('search_fields', value);
      }

      handleURLParams({ search_fields: newSelectedFields }, history);
      setSelectedFields(newSelectedFields);
    }
  }, [
    customRemoveSearchFields,
    customSelectedSearchFields,
    customSetSearchFields,
    history,
    removeFilter,
    selectedFields,
    setFilter,
  ]);

  useEffect(() => {
    if ((customSetSearchFields && customRemoveSearchFields) || external) return;

    setSearchSelectedFields(selectedFields, !!search, itemsPerPage);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFields, setSearchSelectedFields]);

  return (
    <div className="mt-3">
      <h6 className="mb-2">
        {intl.formatMessage({ defaultMessage: 'Search in' })}
      </h6>
      {availableFilters.filter(ft => (ft.key !== '')).map(ft => (
        <FormGroup key={`searchquerytype-${slugify(ft.key)}`}>
          <Label for={`${prefix}checkSearchQueryType-${slugify(ft.key)}`} check className={classnames({ disabled: ft.disabled || ft.locked })}>
            <Input
              type="checkbox"
              name="searchQueryType"
              id={`${prefix}checkSearchQueryType-${slugify(ft.key)}`}
              className="input-check check-s"
              value={ft.key}
              checked={selectedFields?.find(s => (s === ft.key)) || false}
              onChange={handleChangeFilter}
              disabled={!search || ft.disabled || ft.locked}
            />
            {' '}
            <span className="search-field-label">
              {ft.label}
            </span>
            {ft.locked && (
              <span className="ml-1">
                <i className="fal fa-arrow-circle-up" />
              </span>
            )}
            {(ft.disabled && !ft.locked) && (
              <Link to={ft.url} target="_blank" className="ml-1">
                <i className="fal fa-cog" />
              </Link>
            )}
          </Label>
        </FormGroup>
      ))}
    </div>
  );
};

const mapStateToProps = state => ({
  customFields: state.Metadata.customFields,
});

const mapDispatchToProps = dispatch => ({
  setSearchSelectedFields: (searchFields, reload, perPage) => {
    dispatch(setSearchFields(searchFields));

    if (reload) {
      dispatch(clearFilesList());
      dispatch(clearCollectionsList());

      dispatch(fetchFiles());
      dispatch(setCollectionsParams({ per_page: perPage }));
      dispatch(fetchCollections());
    }
  },
  setFilter: (filter) => {
    dispatch(addSelectedFilter(filter));
  },
  removeFilter: (filterType, filterKey) => {
    dispatch(removeSelectedFilter(filterType, filterKey));
  },
});

FiltersSearchFields.propTypes = {
  customFields: PropTypes.array.isRequired,
  setSearchSelectedFields: PropTypes.func.isRequired,
  setFilter: PropTypes.func.isRequired,
  customSetSearchFields: PropTypes.func,
  customRemoveSearchFields: PropTypes.func,
  customSelectedSearchFields: PropTypes.array,
  removeFilter: PropTypes.func.isRequired,
  type: PropTypes.string,
  search: PropTypes.string,
  prefix: PropTypes.string,
  external: PropTypes.bool,
};

FiltersSearchFields.defaultProps = {
  type: null,
  search: null,
  customSetSearchFields: undefined,
  customRemoveSearchFields: undefined,
  customSelectedSearchFields: undefined,
  prefix: '',
  external: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(FiltersSearchFields);
